// The game tables (the office runs them: src/gomoku.ts, rules in src/games/): 五子棋, 黑白棋, 围棋, 中国象棋, 国际象棋,
// one at a time per table. The tables are points in the Tiled map (class table; parseMap → MAP.tables).
// Near a table (the open deck's hearing distance) a small panel shows it: which game (a picker while nobody else
// sits there), the two seats, and the board once a game is on (boards.js draws each). Click the table on the map (or
// 坐下 in the panel) to walk up and sit; two seated otters start a game, seat 1 first. Seated players click the board
// to play (象棋 and chess: the piece, then where it goes); everyone else near the table watches. 再来一局 can switch
// the game. Every name goes in as text.
import { GAMES, OPTIONS, gameInfo, optsLabel, tableLabel, boardOf, REASON, stalemateText, moveCells, pieceSide } from '/boards.js';
import { seatSpot } from '/seats.js';
import { icon, put, label } from '/icons.js';
import { autoTour, tourButton } from '/tours.js';
import { t, tr, onLang, errorText } from '/i18n.js';
import { recordsButton } from '/records.js';
const $ = (id) => document.getElementById(id);
const CSS_URL = new URL('./gomoku.css', import.meta.url).pathname; // its URL: Vite names the built file by its content (vite.config.mjs)
if (!document.querySelector(`link[href="${CSS_URL}"]`)) {
  const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = CSS_URL; document.head.appendChild(l);
}
const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
/** Close enough to sit (tiles), as the office has it (SIT_REACH), with a little margin for the last step. */
const SIT_REACH = 2.3;
const tableDist = (t, x, y) => Math.hypot(x + 0.5 - (t.x + 0.5), y + 0.8 - (t.y + 0.5));

export function initGomoku({ send, me, MAP, mover, toast }) {
  const tables = MAP.tables || [];
  const myId = () => me()?.gid ?? me()?.id;
  /** The table I'm at (its last state from the office), whether each table is busy, a sit waiting for me to get there. */
  let cur = null, deadline = 0, pending = null, folded = false, confirm = null;
  /** The game (and options) I picked at an empty table, sent with my 坐下 (only a player sets a table's game). */
  let pickSel = null;
  /** A chess promotion waiting for its piece: the move without the letter. */
  let promo = null;
  const busy = new Map();

  // ---- the panel
  const panel = el('div', 'gmpanel'); panel.id = 'gmpanel'; panel.style.display = 'none';
  const head = el('div', 'gmhead'), title = el('b', 'gmtitle'), role = el('span', 'gmrole'), fold = el('button', 'gmfold', t('gomoku.fold'));
  head.append(title, role, tourButton(() => 'board-' + kind(), { cls: 'small' }), fold);
  const seats = el('div', 'gmseats'), seatEls = [1, 2].map((c) => {
    const s = el('div', 'gmseat'), stone = el('span', 'gmstone'), who = el('span', 'gmwho'), time = el('span', 'gmtime'), sit = tr(el('button', 'gmsit'), 'gomoku.sit');
    sit.onclick = () => sitDown(cur?.table, c);
    s.append(stone, who, time, sit); seats.append(s);
    return { s, stone, who, time, sit };
  });
  // the game picker (and its options: 禁手, 9/13/19 路): at an empty table I pick and then sit (the first to sit
  // picks); seated alone I can still change it; after a game it's what 再来一局 asks for
  const pick = el('div', 'gmpick'), pickLabel = el('span', 'gmpicklabel'), pickBtns = GAMES.map((g) => {
    const b = label(el('button'), g.icon, g.name); b.dataset.kind = g.kind;
    b.onclick = () => { const c = choice(); applyPick({ kind: g.kind, opts: g.kind === c.kind ? c.opts : {} }); };
    pick.append(b); return b;
  });
  pick.prepend(pickLabel);
  const pickOpts = el('div', 'gmpick gmopts');
  const rematchPick = () => !!cur && cur.phase === 'over' && !!myColor() && !!(cur.seats[0] && cur.seats[1]);
  const aloneSeated = () => !!cur && !!myColor() && cur.phase !== 'playing' && !cur.seats.some((s) => s && s.id !== myId());
  /** What the picker shows: the 再来一局 ask, the table's game (seated), or my pick (not seated yet). */
  function choice() {
    if (!cur) return { kind: 'gomoku', opts: {} };
    if (rematchPick()) return { kind: cur.next ?? cur.kind, opts: cur.nextOpts ?? cur.opts ?? {} };
    if (!myColor() && pickSel) return pickSel;
    return { kind: cur.kind, opts: cur.opts ?? {} };
  }
  function applyPick(sel) {
    if (rematchPick()) send({ t: 'gm_again', table: cur.table, game: sel.kind, opts: sel.opts });
    else if (aloneSeated()) send({ t: 'gm_game', table: cur.table, game: sel.kind, opts: sel.opts });
    else { pickSel = sel; render(); }
  }
  const status = el('div', 'gmstatus');
  const wrap = el('div', 'gmboardwrap'), canvas = el('canvas', 'gmboard'); canvas.id = 'gmboard'; wrap.append(canvas);
  const btns = el('div', 'gmbtns');
  const pass = tr(el('button'), 'gomoku.pass'), resign = el('button', '', t('gomoku.resign')), again = el('button', 'gmprimary', t('gomoku.again')), leave = el('button', '', t('gomoku.leave'));
  btns.append(pass, resign, again, leave);
  // under the board: 显示手数 (number the stones in move order, the last red), 显示坐标 (as agents write moves), both
  // remembered on this device; 复制棋谱 (every move in order, in the game's notation)
  const tools = el('div', 'gmtools'), numsBtn = tr(el('button'), 'gomoku.nums'), coordsBtn = tr(el('button'), 'gomoku.coords'), copyBtn = tr(el('button'), 'gomoku.copy');
  tools.append(numsBtn, coordsBtn, copyBtn, recordsButton(() => kind(), '')); // 战绩 / 排行榜 (public/records.js)
  const pref = (k) => { try { return localStorage.getItem('deck.board.' + k) === '1'; } catch { return false; } };
  const setPref = (k, on) => { try { localStorage.setItem('deck.board.' + k, on ? '1' : '0'); } catch {} };
  let showNums = pref('nums'), showCoords = pref('coords');
  numsBtn.onclick = () => { showNums = !showNums; setPref('nums', showNums); render(); };
  coordsBtn.onclick = () => { showCoords = !showCoords; setPref('coords', showCoords); render(); };
  copyBtn.onclick = async () => {
    const text = recordText(); if (!text) return;
    try { await navigator.clipboard.writeText(text); toast(t('gomoku.copied')); } catch { toast(text.length > 200 ? text.slice(0, 200) + '…' : text); }
  };
  // a pawn reaching the last rank: which piece (the office takes q / r / b / n)
  const promoRow = el('div', 'gmpick gmpromo');
  // the piece names: gomoku.promo.<letter>
  const promoLabel = tr(el('span', 'gmpicklabel'), 'gomoku.promo'), promoBtns = ['q', 'r', 'b', 'n'].map((l) => {
    const b = label(el('button'), 'chess_' + l, t(`gomoku.promo.${l}`)); b.dataset.piece = l; b.onclick = () => { if (promo && myTurn()) send({ t: 'gm_move', table: cur.table, move: promo + l }); promo = null; render(); }; return b;
  });
  promoRow.append(promoLabel, ...promoBtns);
  const body = el('div', 'gmbody'); body.append(pick, pickOpts, seats, status, wrap, promoRow, tools, btns);
  panel.append(head, body);
  document.body.appendChild(panel);
  fold.onclick = () => { folded = !folded; render(); };
  // Two steps for anything that loses the game: the first click asks, a second within 3 s does it.
  const twoStep = (key, fn) => () => {
    if (confirm?.key === key && Date.now() < confirm.until) { confirm = null; fn(); }
    else confirm = { key, until: Date.now() + 3000 };
    render();
  };
  resign.onclick = twoStep('resign', () => send({ t: 'gm_resign', table: cur.table }));
  leave.onclick = () => (cur?.phase === 'playing' ? twoStep('leave', stand)() : stand());
  again.onclick = () => send({ t: 'gm_again', table: cur.table, game: cur.next ?? cur.kind });
  pass.onclick = () => { if (myTurn()) send({ t: 'gm_move', table: cur.table, move: 'pass' }); };
  function stand() { send({ t: 'gm_stand', table: cur.table }); }

  // ---- the board (boards.js draws it; the office checks every move)
  let hover = -1, sel = -1;
  const kind = () => cur?.kind ?? 'gomoku', B = () => boardOf(kind());
  const pieces = () => kind() === 'xiangqi' || kind() === 'chess';
  const myColor = () => (cur ? (cur.seats[0]?.id === myId() ? 1 : cur.seats[1]?.id === myId() ? 2 : 0) : 0);
  const myTurn = () => cur?.phase === 'playing' && myColor() === cur.turn;
  const side = (c) => gameInfo(kind()).sides[c - 1];
  /** The legal moves (where the office lists them) from cell `from`: target cell → move string. */
  function targetsFrom(from) {
    const out = new Map();
    if (!myTurn() || from < 0 || !cur.legal) return out;
    for (const mv of cur.legal) { const fc = moveCells(kind(), mv); if (fc && fc[0] === from && !out.has(fc[1])) out.set(fc[1], mv); }
    return out;
  }
  function cellAt(e) {
    const b = B(), ui = { flip: flip(), coords: showCoords, n: goSize() }, [bw, bh] = b.size(ui), w = canvas.clientWidth || bw, h = canvas.clientHeight || bh; // offsetX/Y: inside the border
    return b.cellAt((e.offsetX / w) * bw, (e.offsetY / h) * bh, ui);
  }
  /** 象棋 and chess from the black side: my pieces at the bottom. */
  const flip = () => pieces() && myColor() === 2;
  canvas.addEventListener('pointermove', (e) => { const h = myTurn() ? cellAt(e) : -1; if (h !== hover) { hover = h; draw(); } });
  canvas.addEventListener('pointerleave', () => { hover = -1; draw(); });
  canvas.addEventListener('click', (e) => {
    const at = cellAt(e);
    if (!myTurn() || at < 0) return;
    const k = kind(), board = cur.view?.board ?? '';
    if (pieces()) {
      const t = targetsFrom(sel);
      if (sel >= 0 && t.has(at)) {
        const mv = t.get(at);
        if (k === 'chess' && mv.length === 5) promo = mv.slice(0, 4); // pick the piece first
        else send({ t: 'gm_move', table: cur.table, move: mv });
        sel = -1; render(); return;
      }
      else sel = pieceSide(board[at]) === myColor() && sel !== at ? at : -1;
      draw(); return;
    }
    if (board[at] !== '0') return;
    const W = width(), mv = `${at % W} ${Math.floor(at / W)}`;
    if (k === 'othello' && cur.legal && !cur.legal.includes(mv)) { toast(t('gomoku.err.othello')); return; }
    if (forbid().includes(at)) { toast(t('gomoku.err.forbidden')); return; }
    send({ t: 'gm_move', table: cur.table, move: mv });
  });
  function draw() {
    const b = B(), [bw, bh] = b.size({ coords: showCoords, n: goSize() }), dpr = Math.max(1, Math.min(3, devicePixelRatio || 1)), pw = Math.round(bw * dpr), ph = Math.round(bh * dpr);
    if (canvas.width !== pw || canvas.height !== ph) { canvas.width = pw; canvas.height = ph; canvas.style.aspectRatio = `${bw} / ${bh}`; canvas.style.width = `${bw}px`; }
    const g = canvas.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const mine = myTurn(), k = kind();
    const dots = new Set(mine && k === 'othello' && cur.legal ? cur.legal.map((mv) => { const m = /^(\d+) (\d+)$/.exec(mv); return m ? +m[2] * 8 + +m[1] : -1; }).filter((i) => i >= 0) : []);
    const nums = showNums && stoneGame() ? stoneNumbers() : new Map();
    b.draw(g, cur?.view ? cur : null, { n: goSize(), forbid: forbid(), coords: showCoords, nums, lastNo: (cur?.order ?? []).findLastIndex((mv) => /^\d+ \d+$/.test(mv)) + 1, flip: flip(), hover: mine && !pieces() && k !== 'othello' ? hover : k === 'othello' && dots.has(hover) ? hover : -1, sel: mine ? sel : -1, targets: new Set(mine ? targetsFrom(sel).keys() : []), dots, side: myColor() });
  }

  /** 五子棋 / 黑白棋 / 围棋: moves are points ("x y"). */
  const stoneGame = () => !pieces();
  const goSize = () => (kind() === 'go' ? cur?.view?.size ?? 9 : undefined);
  const width = () => ({ gomoku: 15, go: goSize(), othello: 8 }[kind()] ?? 15);
  /** 禁手 on and black to move: the points black may not play (the office lists them). */
  const forbid = () => (kind() === 'gomoku' && cur?.phase === 'playing' && cur.turn === 1 ? cur.view?.forbidden ?? [] : []);
  /** Each stone on the board → the number of the move that put it there (a point played again after a capture: the
   *  later number; passes count as moves). */
  function stoneNumbers() {
    const out = new Map(), W = width(), board = cur?.view?.board ?? '';
    (cur?.order ?? []).forEach((mv, i) => { const m = /^(\d+) (\d+)$/.exec(mv); if (m) out.set(+m[2] * W + +m[1], i + 1); });
    for (const [c] of out) if (board[c] === '0') out.delete(c);
    return out;
  }
  /** 复制棋谱: "1. 7,7  2. 8,8 …" (x,y for the stone games; ICCS / UCI for 象棋 / chess), with a header line. */
  function recordText() {
    const order = cur?.order ?? []; if (!order.length) return '';
    const info = gameInfo(kind()), who = [1, 2].map((c) => `${side(c)} ${cur.seats[c - 1]?.name ?? '?'}`).join(' vs ');
    const moves = order.map((mv, i) => `${i + 1}. ${/^\d+ \d+$/.test(mv) ? mv.replace(' ', ',') : mv}`).join('  ');
    return `${info.name} · ${cur.name} · ${who}\n${moves}`;
  }

  // ---- sitting down: from here, or walk up first and sit on arrival
  function sitDown(id, color) {
    const t = tables.find((x) => x.id === id), m = me(); if (!t || !m) return;
    const pickNow = cur?.table === t.id && pickSel && !cur.seats.some(Boolean) ? pickSel : null;
    pending = { id: t.id, color, until: Date.now() + 20000, game: pickNow?.kind, opts: pickNow?.opts };
    if (tableDist(t, m.x, m.y) <= SIT_REACH) { pending.tried = Date.now(); send({ t: 'gm_sit', table: t.id, color, game: pending.game, opts: pending.opts }); return; }
    // to my seat's chair (black / red / chess white left, the other right; with no colour asked, the free one):
    // public/seats.js, the same spot the office puts me on when I sit
    const st = cur?.table === t.id ? cur : null, right = color === 2 || (!color && !!st?.seats[0] && !st.seats[1]);
    const spot = seatSpot(MAP, t, right ? 2 : 1);
    const request = pending;
    const planning = spot ? mover.travelTo(spot.tx, spot.ty, true, [spot.x, spot.y]) : mover.travelTo(Math.round(t.x), t.y, false, null);
    void planning.then((started) => { if (!started && pending === request) pending = null; });
  }
  /** A click on the map at tile (tx, ty): on a table, walk up and sit (or just walk up to watch if it's full). */
  function tableClick(tx, ty) {
    const tb = tables.find((x) => Math.abs(tx - x.x) <= 1 && ty === x.y); if (!tb) return false;
    const st = cur?.table === tb.id ? cur : null, mine = myId();
    if (st && st.seats.some((s) => s?.id === mine)) return true; // already sitting here
    if (cur && myColor()) { toast(t('gomoku.err.seatedElsewhere')); return true; } // walking off would lose the game
    if (st && st.seats[0] && st.seats[1]) return false;          // full: walk there and watch
    sitDown(tb.id);
    return true;
  }

  // ---- showing it
  function seatLine(c) {
    const s = cur.seats[c - 1], e = seatEls[c - 1], mine = s?.id === myId();
    const playing = cur.phase === 'playing', toMove = playing && cur.turn === c;
    e.s.classList.toggle('turn', toMove);
    e.s.classList.toggle('won', cur.phase === 'over' && cur.winner === c);
    e.stone.className = 'gmstone ' + gameInfo(kind()).seat[c - 1];
    const who = s ? (mine ? t('gomoku.seat.me', { name: s.name }) : s.name) : t('gomoku.seat.empty', { side: side(c) });
    // held: dropped off or walked off, the seat waits for them (HOLD_MS, 3 min)
    e.who.textContent = s && cur.away?.[c - 1] ? t('board.away.seat', { name: who }) : who;
    e.who.classList.toggle('empty', !s);
    e.sit.style.display = !s && !myColor() && !playing ? '' : 'none';
    put(e.time, toMove ? 'timer' : null, toMove ? String(Math.max(0, Math.ceil((deadline - Date.now()) / 1000))) : cur.phase === 'over' && cur.winner === c ? t('gomoku.seat.won') : '');
    e.time.classList.toggle('hurry', toMove && deadline - Date.now() <= 10_000);
  }
  /** The tally after a status line, as message vars: captures (围棋 while playing), the score (黑白棋, 围棋 counted), or none. */
  function tally() {
    const sides = { side1: side(1), side2: side(2) };
    if (kind() === 'go' && cur.phase === 'playing') { const c = cur.view?.caps ?? [0, 0]; return c[0] || c[1] ? { tally: 'caps', ...sides, n1: c[0], n2: c[1] } : { tally: 'none' }; }
    if (kind() === 'go' && cur.reason !== 'score') return { tally: 'none' }; // the area count only means something after two passes
    const sc = cur.score ?? cur.view?.score;
    if (!sc || (kind() !== 'othello' && kind() !== 'go')) return { tally: 'none' };
    return { tally: 'score', ...sides, n1: sc[0], n2: sc[1] };
  }
  function statusText() {
    const mc = myColor(), name = (c) => cur.seats[c - 1]?.name ?? t('gomoku.side', { side: side(c) }), gn = gameInfo(kind()).name, v = cur.view ?? {};
    if (cur.phase === 'waiting') {
      const ol = optsLabel(kind(), cur.opts), label = `${gn}${ol ? ' ' + ol : ''}`;
      if (!mc && pickSel && !cur.seats.some(Boolean)) {
        const c = choice(), opt = (OPTIONS[c.kind] ?? []).map((o) => o.values.find(([v]) => v === (c.opts?.[o.key] ?? o.values[0][0]))?.[1]).filter(Boolean).join(' ');
        return t('gomoku.st.picked', { game: `${gameInfo(c.kind).name}${opt ? ' ' + opt : ''}` });
      }
      return mc ? t('gomoku.st.waitOpponent', { game: label }) : t('gomoku.st.waitPlayers', { game: label, side: side(1) });
    }
    if (cur.phase === 'playing' && mc && cur.away?.[mc - 1]) return t('board.away.me');
    if (cur.phase === 'playing' && cur.away?.[cur.turn - 1]) return t('board.away.wait', { name: name(cur.turn) });
    if (cur.phase === 'playing') {
      // one message each (docs/i18n.md): what just happened (pre), whose move, how to play, the tally
      const pre = v.skipped ? 'skipped' : kind() === 'go' && v.passes && mc ? 'passed' : v.check ? 'check' : 'none', skip = v.skipped ? side(v.skipped) : '';
      const vars = { pre, skip, ...tally() };
      if (mc && cur.turn === mc) return t('gomoku.st.yourTurn', { ...vars, side: side(mc), how: pieces() ? 'piece' : kind() === 'go' ? 'go' : 'stone', forbidden: mc === 1 && forbid().length ? 'yes' : 'no' });
      if (mc) return t('gomoku.st.waitMove', { ...vars, name: name(cur.turn) });
      return t('gomoku.st.thinking', { ...vars, side: side(cur.turn), name: name(cur.turn) });
    }
    const why = cur.reason === 'stalemate' ? stalemateText(kind()) : REASON[cur.reason] ?? cur.reason;
    if (cur.winner === 0) return t('gomoku.st.draw', { why, ...tally() });
    const loser = cur.winner === 1 ? 2 : 1, onBoard = !['resign', 'timeout', 'left'].includes(cur.reason);
    const how = onBoard ? why : t('gomoku.st.sideWhy', { side: side(loser), why });
    if (mc) return t(cur.winner === mc ? 'gomoku.st.won' : 'gomoku.st.lost', { how, ...tally() });
    return t('gomoku.st.winner', { side: side(cur.winner), name: name(cur.winner), how, ...tally() });
  }
  function render() {
    const show = !!cur && !!me();
    panel.style.display = show ? '' : 'none';
    if (!show) return;
    const mc = myColor(), full = !!(cur.seats[0] && cur.seats[1]);
    if (confirm && Date.now() >= confirm.until) confirm = null;
    const info = gameInfo(kind());
    const ol = optsLabel(kind(), cur.opts);
    put(title, info.icon, `${info.name}${ol ? ' ' + ol : ''} · ${cur.name}`);
    role.textContent = mc ? t('gomoku.role.player', { side: side(mc) }) : cur.phase === 'playing' ? t('gomoku.role.watching') : '';
    // the picker: nobody else seated and no game on (the first to sit picks), or 再来一局 for the two players
    const others = cur.seats.some((s) => s && s.id !== myId()), rp = rematchPick();
    if (others && !mc) pickSel = null;
    pick.style.display = (cur.phase !== 'playing' && !others) || rp ? '' : 'none';
    pickLabel.textContent = t(rp ? 'gomoku.pick.again' : 'gomoku.pick.label');
    const ch = choice();
    for (const b of pickBtns) b.classList.toggle('on', b.dataset.kind === ch.kind);
    pickOpts.replaceChildren(...(OPTIONS[ch.kind] ?? []).flatMap((o) => o.values.map(([v, label]) => {
      const b = el('button', (ch.opts?.[o.key] ?? o.values[0][0]) === v ? 'on' : '', label);
      b.onclick = () => applyPick({ kind: ch.kind, opts: { ...ch.opts, [o.key]: v } }); return b;
    })));
    pickOpts.style.display = pick.style.display !== 'none' && pickOpts.childElementCount ? '' : 'none';
    promoRow.style.display = promo && myTurn() ? '' : 'none';
    panel.classList.toggle('big', goSize() === 19);
    role.style.display = role.textContent ? '' : 'none';
    fold.textContent = t(folded ? 'gomoku.unfold' : 'gomoku.fold');
    body.style.display = folded ? 'none' : '';
    seatLine(1); seatLine(2);
    { const t = statusText(); put(status, cur.phase === 'over' && cur.winner && cur.winner === myColor() ? 'party' : null, t); }
    status.classList.toggle('mine', myTurn());
    status.classList.toggle('good', cur.phase === 'over' && !!mc && cur.winner === mc);
    // the board once there is something on it, or for the players
    wrap.style.display = cur.phase !== 'waiting' || mc ? '' : 'none';
    canvas.classList.toggle('can', myTurn());
    pass.style.display = mc && cur.phase === 'playing' && (kind() === 'go' || (cur.legal?.length === 1 && cur.legal[0] === 'pass')) ? '' : 'none';
    pass.disabled = !myTurn();
    resign.style.display = mc && cur.phase === 'playing' ? '' : 'none';
    resign.textContent = t(confirm?.key === 'resign' ? 'gomoku.resign.confirm' : 'gomoku.resign');
    again.style.display = mc && cur.phase === 'over' && full ? '' : 'none';
    const iAsked = cur.again.includes(myId()), theyAsked = cur.again.length > 0 && !iAsked, nextName = cur.next ? gameInfo(cur.next).name : '';
    again.textContent = iAsked ? t('gomoku.again.waiting') : theyAsked ? (nextName ? t('gomoku.again.theyAskGame', { game: nextName }) : t('gomoku.again.theyAsk')) : nextName ? t('gomoku.again.askGame', { game: nextName }) : t('gomoku.again.ask');
    again.disabled = iAsked;
    leave.style.display = mc ? '' : 'none';
    tools.style.display = wrap.style.display;
    numsBtn.style.display = stoneGame() ? '' : 'none';
    numsBtn.classList.toggle('on', showNums); coordsBtn.classList.toggle('on', showCoords);
    copyBtn.disabled = !cur.order?.length;
    leave.textContent = t(confirm?.key === 'leave' ? 'gomoku.leave.confirm' : 'gomoku.leave');
    btns.style.display = mc ? '' : 'none';
    draw();
    if (mc && cur.phase === 'playing' && !folded) autoTour('board-' + kind()); // the first game of each kind: its coach marks (public/tours.js)
  }
  // the language changed: the panel's words (the static buttons and the promotion label: tr), the piece names
  onLang(() => { for (const b of pickBtns) { const g = GAMES.find((x) => x.kind === b.dataset.kind); label(b, g.icon, g.name); } for (const b of promoBtns) label(b, 'chess_' + b.dataset.piece, t(`gomoku.promo.${b.dataset.piece}`)); render(); });
  setInterval(() => {
    const m = me();
    if (pending && (Date.now() > pending.until || !m)) pending = null;
    if (pending && m) {
      // arrived: sit (again each second until the office has me seated: my last step may reach it after this)
      const t = tables.find((x) => x.id === pending.id), seatedHere = cur?.table === pending.id && myColor();
      if (seatedHere || (cur?.table === pending.id && cur.seats[0] && cur.seats[1])) pending = null;
      else if (t && !mover.planning() && tableDist(t, m.x, m.y) <= SIT_REACH && Date.now() - (pending.tried || 0) > 1000) { pending.tried = Date.now(); send({ t: 'gm_sit', table: t.id, color: pending.color, game: pending.game, opts: pending.opts }); }
    }
    if (cur && panel.style.display !== 'none') { seatLine(1); seatLine(2); if (confirm && Date.now() >= confirm.until) render(); }
  }, 250);

  function msg(m) {
    if (m.t === 'gm_state') {
      const was = cur?.table === m.table ? cur : null, wasSeated = was && was.seats.some((s) => s?.id === myId());
      if (!was) folded = false;
      if (was && was.game !== m.game) confirm = null;
      if (!was || was.moves !== m.moves || was.game !== m.game || was.kind !== m.kind) { sel = -1; promo = null; }
      if (!was) pickSel = null;
      if (!m.view) m.view = { board: m.board, last: m.last, line: m.line }; // an office from before the other games
      if (!m.kind) m.kind = 'gomoku';
      cur = m; deadline = Date.now() + (m.left || 0);
      if (m.phase === 'playing' && myColor() && (!was || was.phase !== 'playing')) folded = false;
      if (wasSeated && !myColor() && m.reason === 'left' && was.phase === 'playing') toast(t('gomoku.left'));
      busy.set(m.table, { busy: m.phase === 'playing', seated: m.seats.filter(Boolean).length, kind: m.kind, opts: m.opts });
      render();
    } else if (m.t === 'gm_bye') { if (cur?.table === m.table) { cur = null; confirm = null; render(); } }
    else if (m.t === 'gm_tables') { busy.clear(); for (const t of m.list) busy.set(t.id, t); }
    else if (m.t === 'gm_err') { if (!(pending && (m.code === 'reach' || /走到/.test(m.text)))) toast(m.code ? errorText('gomoku.err', { code: m.code, params: m.params, message: m.text }) : m.text); } // (a game's refusal: its code, worded here)
  }
  /** For the map: each table with whether a game is on there. */
  const mapTables = () => tables.map((t) => ({ ...t, busy: !!busy.get(t.id)?.busy, seated: busy.get(t.id)?.seated ?? 0, kindName: tableLabel(busy.get(t.id)?.kind, busy.get(t.id)?.opts) }));
  /** Seated at a table: the way I face (my seat's, as the office keeps it: public/seats.js), else null. */
  function facing() {
    const c = myColor(), t = cur && c ? tables.find((x) => x.id === cur.table) : null, m = me();
    if (!t || !m) return null;
    // on my chair: the seat's way; elsewhere (e.g. seats swapped for 再来一局): toward the table (as the office has it)
    const at = seatSpot(MAP, t, c);
    if (at && Math.hypot(m.x - at.x, m.y - at.y) < 0.35) return at.dir;
    const dx = t.x + 0.5 - (m.x + 0.5), dy = t.y + 0.5 - (m.y + 0.8);
    return Math.abs(dx) >= Math.abs(dy) ? (dx > 0 ? 1 : 3) : dy > 0 ? 0 : 2;
  }
  return { msg, tableClick, tables: mapTables, current: () => cur, facing };
}
